import * as React from "react";
import "./waiting-order-transactions.scss";
import { Page } from "components/page/page";
import { Card } from "components/card/card";
import { Modal } from "components/modal/modal";
import { Button, buttonType } from "components/button/button";
import { Input } from "components/input/input";
import { Dropdown } from "components/dropdown/dropdown";
import { Badge, UserLink, CopyablePhone } from "components";
import {
    CopyOutlined,
    DollarCircleOutlined,
    DownOutlined,
    EditOutlined,
    ExportOutlined,
    InfoCircleOutlined,
} from "@ant-design/icons";
import {
    Dropdown as AntdDropdown,
    Empty,
    Menu,
    Table as AntdTable,
    Tooltip,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import moment from "jalali-moment";
import { ReactComponent as CalendarIcon } from "assets/icons/dash-line-icons/calendar.svg";
import { Enum, Order, PaginationList } from "core/domain";
import {
    confirmAndPayoutOrder,
    getAllOrders,
    getAllOrderStateTypes,
} from "core/repositories/orders-transactions";
import { digitSperator } from "core/utils/decimalExtentions";
import { copyToClipboard } from "core/utils/copyToClipboard";
import { getIconPath } from "core/utils/getIconPath";
import { downloadCSV } from "core/utils/export-to-file";
import { jFormat } from "core/utils/jFormat";
import { OrderDetailModal } from "../order-detail/order-detail";
import { OrderEditModal } from "../edit-modal/edit-modal";
import { ConfirmBulkSelectModal } from "../bulk-select-modal/bulk-select-modal";
import { getOrderTransactionStateBadgeType } from "../script";

// وضعیت‌هایی که یک معامله را «در انتظار اقدام ادمین» می‌کنند. دقیقا همان‌هایی که
// شمارنده‌ی کنار منوی «اقدامات» می‌شمارد، پس عدد بج و تعداد ردیف‌ها هم‌خوان می‌مانند.
export const WAITING_STATE_IDS = [8, 11, 12];

const ALL_WAITING_STATE: Enum = {
    id: -1,
    title: "همه در انتظار",
};

const actionItems: Enum[] = [
    {
        id: 0,
        title: "انتخاب عملیات",
    },
    {
        id: 100,
        title: "تایید و تسویه",
    },
];

interface FilterValues {
    search: string;
    state: Enum;
    startDate: string;
    endDate: string;
}

const empty_filters: FilterValues = {
    search: "",
    state: ALL_WAITING_STATE,
    startDate: moment()
        .locale("fa")
        .subtract(1, "months")
        .format("jYYYY/jMM/jDD"),
    endDate: "",
};

interface Props {
    /** OrderTransactionType — 1: خرید، 2: فروش، 3: ترید */
    orderType: number;
    pageTitle: string;
    cardTitle: string;
    className: string;
    exportFileName: string;
}

export const WaitingOrderTransactions: React.FC<Props> = ({
    orderType,
    pageTitle,
    cardTitle,
    className,
    exportFileName,
}) => {
    const startDateRef = React.useRef();
    const endDateRef = React.useRef();

    const [detailModalIsOpen, setDetailModalIsOpen] =
        React.useState<boolean>(false);
    const [editModalIsOpen, setEditModalIsOpen] =
        React.useState<boolean>(false);
    const [payoutModalIsOpen, setPayoutModalIsOpen] =
        React.useState<boolean>(false);
    const [confirmBulkActionModalIsOpen, setConfirmBulkActionModalIsOpen] =
        React.useState<boolean>(false);

    const [selectedItem, setSelectedItem] = React.useState<Order>();
    const [selectedRows, setSelectedRows] = React.useState<Order[]>();
    const [selectedActionItem, setSelectedActionItem] = React.useState<Enum>(
        actionItems[0],
    );

    const [orderStateTypes, setOrderStateTypes] = React.useState<Enum[]>([]);
    const [orders, setOrders] = React.useState<PaginationList<Order>>();
    const [query, setQuery] = React.useState<string>("");
    const [pageSize, setPageSize] = React.useState<number>(10);
    const [filterValues, setFilterValues] =
        React.useState<FilterValues>(empty_filters);

    const [loading, setLoading] = React.useState<boolean>(false);
    const [buttonLoading, setButtonLoading] = React.useState<boolean>(false);
    const [deleteButtonLoading, setDeleteButtonLoading] =
        React.useState<boolean>(false);
    const [confirmButtonLoading, setConfirmButtonLoading] =
        React.useState<boolean>(false);
    const [actionApproveLoading, setActionApproveLoading] =
        React.useState<boolean>(false);

    // نوع معامله روی هر صفحه ثابت است، پس فقط وضعیت‌های «در انتظار» قابل انتخاب‌اند.
    const waitingStateOptions = React.useMemo<Enum[]>(
        () => [
            ALL_WAITING_STATE,
            ...orderStateTypes.filter((s) => WAITING_STATE_IDS.includes(s.id)),
        ],
        [orderStateTypes],
    );

    const buildQuery = React.useCallback(
        (values: FilterValues) => {
            let _query = `?type=${orderType}`;

            const states =
                values.state?.id === ALL_WAITING_STATE.id
                    ? WAITING_STATE_IDS
                    : [values.state.id];
            states.forEach((state) => (_query += `&state=${state}`));

            if (values.search) _query += `&keywords=${values.search}`;
            if (values.startDate !== "")
                _query += `&startDate=${jFormat(values.startDate)}`;
            if (values.endDate !== "")
                _query += `&endDate=${jFormat(values.endDate)}`;

            return _query;
        },
        [orderType],
    );

    const filterHandle = (clearFilter?: boolean) => {
        const values = clearFilter
            ? { ...empty_filters, startDate: "" }
            : filterValues;
        const appliedQuery = buildQuery(values);

        setQuery(appliedQuery);
        setSelectedRows(undefined);

        getAllOrders(
            clearFilter ? setDeleteButtonLoading : setButtonLoading,
            setOrders,
            1,
            pageSize,
            appliedQuery,
        );
    };

    const clearFiltersHandle = () => {
        setFilterValues({ ...empty_filters, startDate: "" });
        filterHandle(true);
    };

    React.useEffect(() => {
        const fetchInitialData = async () => {
            // این فقط گزینه‌های دراپ‌داون را پر می‌کند؛ اگر setLoading بگیرد،
            // اسپینر جدول چند بار پشت سر هم روشن/خاموش می‌شود.
            const ignoreLoading = () => {};
            await getAllOrderStateTypes(ignoreLoading, setOrderStateTypes);

            const initialQuery = buildQuery(empty_filters);
            setQuery(initialQuery);
            getAllOrders(setLoading, setOrders, 1, 10, initialQuery);
        };

        fetchInitialData();
    }, [buildQuery]);

    React.useEffect(() => {
        confirmBulkActionModalIsOpen === false && setSelectedRows(undefined);
    }, [confirmBulkActionModalIsOpen]);

    const refetch = React.useCallback(
        () =>
            getAllOrders(
                setLoading,
                setOrders,
                orders?.pagination.pageIndex ?? 1,
                pageSize,
                query,
            ),
        [orders?.pagination.pageIndex, pageSize, query],
    );

    const onPayoutIconClickHandle = React.useCallback((item: Order) => {
        setSelectedItem(item);
        setPayoutModalIsOpen(true);
    }, []);

    const openActionConfirmModal = () => {
        setActionApproveLoading(false);
        setConfirmBulkActionModalIsOpen(true);
    };

    const handleTableChange = React.useCallback(
        (pagination: TablePaginationConfig) => {
            const nextPage = pagination.current ?? 1;
            const nextPageSize = pagination.pageSize ?? pageSize;
            const currentPage = orders?.pagination.pageIndex ?? 1;

            if (nextPage === currentPage && nextPageSize === pageSize) return;

            setPageSize(nextPageSize);
            setSelectedRows(undefined);
            getAllOrders(setLoading, setOrders, nextPage, nextPageSize, query);
        },
        [orders?.pagination.pageIndex, pageSize, query],
    );

    const columns = React.useMemo<ColumnsType<Order>>(
        () => [
            {
                title: "شناسه",
                dataIndex: "orderTransactionTrackingCode",
                key: "orderTransactionTrackingCode",
                width: 190,
                render: (trackingCode: string) => (
                    <span className="en copiable">
                        {trackingCode}
                        <Tooltip title="کپی شناسه سفارش">
                            <button
                                type="button"
                                className="table-action-button copy-button"
                                aria-label="کپی شناسه سفارش"
                                onClick={() =>
                                    copyToClipboard(trackingCode, "شناسه سفارش")
                                }
                            >
                                <CopyOutlined />
                            </button>
                        </Tooltip>
                    </span>
                ),
            },
            {
                title: "نام",
                dataIndex: "fullName",
                key: "fullName",
                width: 150,
                render: (fullName: string, row) => (
                    <UserLink userId={row.userId}>{fullName || "-"}</UserLink>
                ),
            },
            {
                title: "موبایل",
                dataIndex: "phoneNumber",
                key: "phoneNumber",
                width: 135,
                render: (phoneNumber: string, row) => (
                    <CopyablePhone phoneNumber={phoneNumber} />
                ),
            },
            {
                title: "جفت ارز",
                key: "pair",
                width: 170,
                render: (_, row) => (
                    <div className="cell pair">
                        <img
                            src={getIconPath(row.sourceSymbol.toLowerCase())}
                            alt=""
                        />
                        <span className="en">{row.sourceSymbol}</span>
                        <span aria-hidden="true"> / </span>
                        <span className="en">{row.destinationSymbol}</span>
                        <img
                            src={getIconPath(
                                row.destinationSymbol.toLowerCase(),
                            )}
                            alt=""
                        />
                    </div>
                ),
            },
            {
                title: "مبلغ مبدا",
                dataIndex: "sourceAmount",
                key: "sourceAmount",
                align: "left",
                width: 130,
                render: (amount: number, row) =>
                    row.sourceSymbol.toLowerCase() === "irt"
                        ? digitSperator(amount.toFixed(0))
                        : amount,
            },
            {
                title: "مبلغ مقصد",
                dataIndex: "destinationAmount",
                key: "destinationAmount",
                align: "left",
                width: 130,
                render: (amount: number, row) =>
                    row.destinationSymbol.toLowerCase() === "irt"
                        ? digitSperator(amount.toFixed(0))
                        : amount,
            },
            {
                title: "تاریخ ثبت سفارش",
                dataIndex: "createdOn",
                key: "createdOn",
                width: 175,
                render: (createdOn: string) => (
                    <span>
                        {moment.utc(createdOn).local().format("HH:mm")}{" "}
                        {moment
                            .utc(createdOn)
                            .locale("fa")
                            .local()
                            .format("YYYY/MM/DD")}
                    </span>
                ),
            },
            {
                title: "وضعیت",
                dataIndex: "state",
                key: "state",
                width: 150,
                render: (state: Enum) => (
                    <Badge
                        text={state.title}
                        type={getOrderTransactionStateBadgeType(state?.id)}
                    />
                ),
            },
            {
                title: "نوع سفارش",
                dataIndex: "type",
                key: "type",
                width: 130,
                render: (type: Enum) => (
                    <Badge
                        text={type.title}
                        type={type.title === "خرید" ? "success" : "error"}
                    />
                ),
            },
            {
                title: "عملیات",
                key: "action",
                fixed: "left",
                width: 140,
                render: (_, row) => (
                    <div className="actions">
                        <Tooltip title="ویرایش معامله">
                            <button
                                type="button"
                                className="table-action-button"
                                aria-label="ویرایش معامله"
                                onClick={() => {
                                    setSelectedItem(row);
                                    setEditModalIsOpen(true);
                                }}
                            >
                                <EditOutlined />
                            </button>
                        </Tooltip>
                        <Tooltip title="تایید و تسویه">
                            <button
                                type="button"
                                className="table-action-button payout-action"
                                aria-label="تایید و تسویه"
                                onClick={() => onPayoutIconClickHandle(row)}
                            >
                                <DollarCircleOutlined />
                            </button>
                        </Tooltip>
                        <Tooltip title="جزئیات معامله">
                            <button
                                type="button"
                                className="table-action-button"
                                aria-label="جزئیات معامله"
                                onClick={() => {
                                    setSelectedItem(row);
                                    setDetailModalIsOpen(true);
                                }}
                            >
                                <InfoCircleOutlined />
                            </button>
                        </Tooltip>
                    </div>
                ),
            },
        ],
        [onPayoutIconClickHandle],
    );

    return (
        <Page
            className={`pmx-waiting-order-transactions ${className}`}
            title={pageTitle}
        >
            <Modal
                setIsOpen={setPayoutModalIsOpen}
                isOpen={payoutModalIsOpen}
                className="confirm-payout-modal"
            >
                <p>
                    آیا از تایید و تسویه تراکنش کاربر {selectedItem?.fullName}{" "}
                    اطمینان دارید؟
                </p>

                <div className="buttons">
                    <Button
                        type={buttonType.info}
                        text="بله"
                        onClick={() =>
                            confirmAndPayoutOrder(
                                setConfirmButtonLoading,
                                {
                                    trackingCode:
                                        selectedItem?.orderTransactionTrackingCode,
                                },
                                () => setPayoutModalIsOpen(false),
                                refetch,
                            )
                        }
                        loading={confirmButtonLoading}
                    />
                    <Button
                        type={buttonType.error}
                        text="خیر"
                        onClick={() => setPayoutModalIsOpen(false)}
                    />
                </div>
            </Modal>
            {selectedRows && (
                <ConfirmBulkSelectModal
                    isOpen={confirmBulkActionModalIsOpen}
                    setIsOpen={setConfirmBulkActionModalIsOpen}
                    buttonLoading={actionApproveLoading}
                    selectedActionItem={selectedActionItem}
                    selectedRows={selectedRows}
                    setButtonLoading={setActionApproveLoading}
                    setSelectedRows={setSelectedRows}
                    fetchData={async () => {
                        await refetch();
                    }}
                    actionItem={selectedActionItem}
                />
            )}
            {/* اسپینر فقط روی خود جدول است؛ blur روی Card باعث دو اسپینر هم‌زمان می‌شد. */}
            <Card title={cardTitle}>
                {selectedItem && (
                    <OrderDetailModal
                        title="جزئیات معامله"
                        selectedRow={selectedItem}
                        isOpen={detailModalIsOpen}
                        setIsOpen={setDetailModalIsOpen}
                        trackingId={selectedItem?.orderTransactionTrackingCode}
                    />
                )}
                {selectedItem && orderStateTypes.length > 0 && (
                    <OrderEditModal
                        orderStates={orderStateTypes}
                        isOpen={editModalIsOpen}
                        setIsOpen={setEditModalIsOpen}
                        item={selectedItem}
                        onClose={refetch}
                    />
                )}
                <div className="filter-row">
                    <div className="fields">
                        <Input
                            value={filterValues.search}
                            className="search"
                            placeholder="جستجو کنید ..."
                            onPressEnter={filterHandle}
                            name="search"
                            onChange={(e) =>
                                setFilterValues({
                                    ...filterValues,
                                    search: e.target.value,
                                })
                            }
                        />

                        {waitingStateOptions.length > 1 && (
                            <Dropdown
                                items={waitingStateOptions}
                                onChangeHandle={(value) =>
                                    setFilterValues({
                                        ...filterValues,
                                        state: waitingStateOptions.find(
                                            (l) => l.id === value,
                                        ),
                                    })
                                }
                                selectedOption={filterValues.state}
                            />
                        )}

                        <DatePicker
                            className="date-picker"
                            value={filterValues.startDate}
                            calendar={persian}
                            ref={startDateRef}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input
                                        value={filterValues.startDate}
                                        icon={<CalendarIcon />}
                                        placeholder="از تاریخ"
                                    />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({
                                    ...filterValues,
                                    startDate: e.format(),
                                });
                            }}
                        />
                        <DatePicker
                            className="date-picker"
                            value={filterValues.endDate}
                            calendar={persian}
                            ref={endDateRef}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input
                                        value={filterValues.endDate}
                                        icon={<CalendarIcon />}
                                        placeholder="تا تاریخ"
                                    />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({
                                    ...filterValues,
                                    endDate: e.format(),
                                });
                            }}
                        />
                    </div>
                    <div className="buttons">
                        <Button
                            text="خروجی اکسل"
                            type={buttonType.info}
                            icon={<ExportOutlined />}
                            onClick={() =>
                                downloadCSV(exportFileName, orders?.items ?? [])
                            }
                        />
                        <Button
                            loading={buttonLoading}
                            disabled={buttonLoading}
                            text="اعمال فیلتر"
                            type={buttonType.success}
                            onClick={() => filterHandle()}
                        />
                        <Button
                            loading={deleteButtonLoading}
                            disabled={deleteButtonLoading}
                            text="حذف فیلتر"
                            type={buttonType.error}
                            onClick={clearFiltersHandle}
                        />
                    </div>
                </div>
                {selectedRows && selectedRows?.length > 1 && (
                    <div className="action-row">
                        <label htmlFor="">عملیات: </label>
                        <AntdDropdown
                            trigger={["click", "hover"]}
                            overlay={
                                <Menu>
                                    {actionItems &&
                                        actionItems.map((state) => (
                                            <Menu.Item
                                                onClick={() =>
                                                    setSelectedActionItem(state)
                                                }
                                            >
                                                {state.title}
                                            </Menu.Item>
                                        ))}
                                </Menu>
                            }
                            placement="bottom"
                        >
                            <Button
                                icon={<DownOutlined />}
                                type={buttonType.outlined}
                                text={selectedActionItem.title}
                            />
                        </AntdDropdown>
                        <Button
                            disabled={selectedActionItem.id === 0}
                            type={buttonType.info}
                            text="اصلاح تراکنش‌های انتخاب شده"
                            onClick={() => openActionConfirmModal()}
                        />
                        <Button
                            type={buttonType.outlined}
                            text={`${selectedRows?.length} آیتم انتخاب شده`}
                        />
                    </div>
                )}
                <AntdTable<Order>
                    className="orders-transactions-table"
                    rowKey="orderTransactionTrackingCode"
                    columns={columns}
                    dataSource={orders?.items ?? []}
                    loading={loading}
                    locale={{
                        emptyText: <Empty description="رکوردی یافت نشد" />,
                    }}
                    rowSelection={{
                        selectedRowKeys:
                            selectedRows?.map(
                                (order) => order.orderTransactionTrackingCode,
                            ) ?? [],
                        onChange: (_, selected) => setSelectedRows(selected),
                    }}
                    scroll={{ x: 1500 }}
                    pagination={{
                        current: orders?.pagination.pageIndex ?? 1,
                        pageSize: orders?.pagination.pageSize ?? pageSize,
                        total: orders?.pagination.totalCount ?? 0,
                        showSizeChanger: true,
                        pageSizeOptions: [10, 20, 50, 100, 1000, 10000, 100000],
                        showTotal: (total, range) =>
                            `نمایش ${digitSperator(range[0])} تا ${digitSperator(range[1])} از ${digitSperator(total)} رکورد`,
                    }}
                    onChange={handleTableChange}
                />
            </Card>
        </Page>
    );
};
